// src/hooks/PageContext.tsx
import { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';

// === Остальные хуки ===
import { useAccountContext } from './useAccountContext';
import { useAdminContext } from './useAdminContext';
import { useBlogContext } from './BlogContext/index';
import { useDistContext } from './useDistContext';
import { useHomeContext } from './useHomeContext';

// === Запросы ===
import { ME } from '../graphql/queries';

// === Типы ===
interface PageContextType {
  page: 'home' | 'blog' | 'account' | 'admin' | 'dist' | 'instructions' | 'svor';
  isAuthenticated: boolean;
  userRole: string | null;
  data?: any;
  actions?: any;
}

const PageContext = createContext<PageContextType | null>(null);

export const PageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const path = location.pathname.toLowerCase();

  // ✅ НАДЕЖНЫЙ СПОСОБ: парсим строку адреса напрямую, без зависимости от Route
  // Ожидаем формат: /account/edit/64f1a2b3c4d5e6f7a8b9c0d1
  const pathParts = path.split('/');
  const isEditPage = pathParts[1] === 'account' && pathParts[2] === 'edit';
  const userId = isEditPage ? pathParts[3] : undefined;

  // 🔍 ОТЛАДКА: посмотрите в консоль браузера (F12), что здесь выводится при клике на пользователя
  console.log('🔍 DEBUG PageContext -> path:', path, '| userId:', userId);

  const { data: meData } = useQuery(ME);
  const isAuthenticated = !!meData?.me;
  const userRole = meData?.me?.role || null;

  const blogContext = useBlogContext();
  
  // ✅ Передаем { id: userId } ТОЛЬКО если userId реально существует
  const accountContext = useAccountContext(userId ? { id: userId } : undefined);
  
  const adminContext = useAdminContext();
  const distContext = useDistContext(userRole);
  const homeContext = useHomeContext(userRole);

  const value = useMemo((): PageContextType => {
    if (path === '/' || path.includes('home')) {
      return {
        page: 'home',
        isAuthenticated,
        userRole,
        data: { activeContent: homeContext.activeContent, loading: homeContext.loading },
        actions: homeContext,
      };
    }
    if (path.includes('blog')) {
      return {
        page: 'blog',
        isAuthenticated,
        userRole,
        data: { notes: blogContext.notes, loading: blogContext.loading },
        actions: blogContext,
      };
    }
    if (path.includes('account')) {
      return {
        page: 'account',
        isAuthenticated,
        userRole,
        data: accountContext.data,
        actions: accountContext,
      };
    }
    if (path.includes('administration')) {
      return {
        page: 'admin',
        isAuthenticated,
        userRole,
        data: adminContext.data,
        actions: adminContext,
      };
    }
    if (path.includes('dist')) {
      return {
        page: 'dist',
        isAuthenticated,
        userRole,
        data: { activeContent: distContext.activeContent, loading: distContext.loading },
        actions: distContext,
      };
    }
    if (path.includes('instructions')) {
      return { page: 'instructions', isAuthenticated, userRole };
    }
    if (path.includes('svor')) {
      return { page: 'svor', isAuthenticated, userRole };
    }

    return { page: 'home', isAuthenticated, userRole };
  }, [
    path,
    userId, // ✅ ВАЖНО: пересчитываем при смене userId
    blogContext,
    accountContext,
    adminContext,
    isAuthenticated,
    userRole,
    distContext,
    homeContext,
  ]);

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>;
};

export const usePageContext = () => {
  const context = useContext(PageContext);
  if (!context) throw new Error('usePageContext must be used within PageProvider');
  return context;
};